import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { type TContentBlockBase } from '@onesource/schemas';
import { useCompleteBlock } from '@onesource/services';
import DOMPurify from 'dompurify';
import { useEffect, useRef } from 'react';

interface EmbedBlockContentProps {
  block: TContentBlockBase;
}

const sanitizeEmbedHtml = (raw: string) =>
  DOMPurify.sanitize(raw, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'src', 'width', 'height', 'sandbox', 'referrerpolicy'],
  });

/**
 * Renders a single embed content block (URL iframe or sanitized embed code)
 * directly on the lesson page. Used by the INLINE_DOCUMENT lesson template so
 * the embed shows in place instead of on a dedicated block page.
 *
 * Embed is a view block: it completes once it first scrolls into the viewport,
 * so blocks sharing the inline page don't all complete on load.
 */
export function EmbedBlockContent({ block }: EmbedBlockContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackedRef = useRef(false);
  const { mutate: completeBlock } = useCompleteBlock();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !trackedRef.current) {
          trackedRef.current = true;
          completeBlock(block.id);
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [block.id, completeBlock]);

  const raw = block.body?.trim() ?? '';
  const isUrl = /^https?:\/\//i.test(raw);

  return (
    <Box ref={containerRef} sx={{ pt: 2, pb: 2, px: { xs: 2, md: 3 }, background: 'white', borderRadius: '15px' }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: 9, md: 16 }, mb: 2 }}>
        {block.title || 'Embed'}
      </Typography>

      {raw ? (
        <Box
          sx={{
            width: '100%',
            borderRadius: '13px',
            overflow: 'hidden',
            bgcolor: 'grey.100',
            '& iframe': { display: 'block', width: '100%', minHeight: { xs: 320, md: 600 }, border: 0 },
          }}
        >
          {isUrl ? (
            <Box
              component="iframe"
              src={raw}
              title={block.title || 'Embedded content'}
              sx={{ width: '100%', height: { xs: 320, md: 600 }, border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              allowFullScreen
            />
          ) : (
            <Box dangerouslySetInnerHTML={{ __html: sanitizeEmbedHtml(raw) }} />
          )}
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary">
          No embed content available.
        </Typography>
      )}
    </Box>
  );
}
